import { gql } from '@apollo/client';

export const SURVEY_BY_CODE = gql`
  query surveyByCode($code: String!, $visitorId: String!) {
    surveyByCode(code: $code, visitorId: $visitorId) {
      datasheetVersionId
      jobId
      countryId
      surveySkin {
        deletedAt
        header
        id
        introTemplate
        acknowledgmentTemplate
        name
        updatedAt
      }
      surveyData {
        id
        isDuplicateVisitorId
        isFinished
        versionId
        dashboardLink
        publicLink
        endDate
        prizeDrawUrl
        restrictToOneResponse
        allowToResume
        effectiveDate
        name
        languages {
          code
          deletedAt
          id
          name
        }

        questions {
          id
          datasheetQuestionId
          text
          questionNumber
          descriptionAlias
          questionType
          fileId
          fileUrl
          conditions {
            action
            datasheetQuestionId
            id
            logicalOperator
            position
            predicate
            predicateAddition
            rangeEnd
            rangeStart
            rowName
            value
          }
          validations {
            rule
            text
          }
          translations {
            descriptionAlias
            id
            languageId
            options
            text
          }
          autofillSettings {
            field
            id
          }
          currencySettings {
            currencies
            currencyItems {
              code
              id
              name
              symbol
            }
            id
          }
          dependentDropdownSettings {
            allowMultiSelect
            dependsOn
            id
            options {
              definition
              label
              parent
            }
          }
          dropdownSettings {
            allowMultiSelect
            id
            options {
              definition
              value
            }
          }
          fileSettings {
            fileSize
            fileType
            id
            isJobTemplate
          }
          numberSettings {
            id
            precision
            removeDecimalLimitation
          }
          relationSettings {
            allowMultiSelect
            entity
            entityItems
            id
          }
          smileyRatingSettings {
            available
            text
          }
          starRatingSettings {
            id
            numberOfStarsAvailable
            rows
          }
          tableSettings {
            columns {
              allowMultiSelect
              currencies
              currencyItems {
                code
                id
                name
                symbol
              }
              entity
              entityItems
              fileSize
              fileType
              isMaximized
              isTextarea
              name
              number
              options {
                definition
                value
              }
              precision              
              removeDecimalLimitation
              type              
              dependsOn
              dependentDropdownOptions {
                definition
                label
                parent
              }
            }
            enableMultipleRows
            fixedValues {
              columnName
              value
            }
            id
            maximizeWidth
          }
          textSettings {
            id
            isMaximized
            isTextarea
          }
          radioButtonSettings {
            multipleAnswer
            rows
          }
          pictureSettings {
            allowMultiSelect
            id
            options {
              label
              fileId
              fileUrl
            } 
          }
          voiceSettings {
            allowMultiSelect
            id
            options {
              label
              fileId
              fileUrl
            } 
          }
        }
      }
    }
  }
`;

export const SURVEY_RESPONSES_BY_HASH = gql`
  query surveyResponsesByHash($hash: String!) {
    surveyResponsesByHash(hash: $hash) {
      finishedAt
      results {
        datasheetQuestionId
        answeredAt
        result
      }
    }
  }
`;

export const SURVEY_RESPONSES_BY_DATASHEET = gql`
  query surveyResponses(
    $jobId: Int!
    $datasheetVersionId: Int!
    $responseType: [SurveyResponseType!]
    $invalidFalsificationRule: [FalsificationRuleType!]
    $limit: Int
    $offset: Int
  ) {
    surveyResponses(
      jobId: $jobId
      datasheetVersionId: $datasheetVersionId
      responseType: $responseType
      invalidFalsificationRules: $invalidFalsificationRule
      limit: $limit
      offset: $offset
    ) {
      id
      responseNumber
      responseTime
      timezone
      startedAt
      finishedAt
      lastActivityAt
      responderOs
      browser
      responseType
      invalidFalsificationRules
      manualOverridenTypeComment
      results {
        datasheetQuestionId
        questionNumber
        answeredAt
        result
        resultNumber
      }
    }
    surveyResponsesPageInfo(
      jobId: $jobId
      datasheetVersionId: $datasheetVersionId
      responseType: $responseType
      invalidFalsificationRules: $invalidFalsificationRule
    ) {
      totalCount
    }
  }
`;

export const SURVEY_SUMMARY = gql`
  query surveySummary($jobId: Int!, $datasheetVersionId: Int!, $fromDate: DateTime, $toDate: DateTime, $excludeInvalidResponse: Boolean) {
    surveySummary(jobId: $jobId, datasheetVersionId: $datasheetVersionId, fromDate: $fromDate, toDate: $toDate, excludeInvalidResponse: $excludeInvalidResponse) {
      jobId
      datasheetVersionId
      totalResponses
      completed
      completionRate
      dropout
      averageTimeSeconds
      viewsCount
      responseStatistics {
        questionType
        questionNumber
        datasheetQuestionId
        text
        numberOfResponses
        optionStats {
          option
          optionIndex
          responseCount
          responsePercent
        }
      }
    }
  }
`;

export const SURVEY_SUMMARY_BY_CODE = gql`
  query surveySummaryByCode($code: String!, $fromDate: DateTime, $toDate: DateTime, $excludeInvalidResponse: Boolean) {
    surveySummaryByCode(code: $code, fromDate: $fromDate, toDate: $toDate,  excludeInvalidResponse: $excludeInvalidResponse) {
      jobId
      datasheetVersionId
      reportNumber
      totalResponses
      completed
      completionRate
      dropout
      averageTimeSeconds
      viewsCount
      responseStatistics {
        datasheetQuestionId
        text
        questionType
        questionNumber
        numberOfResponses
        optionStats {
          option
          optionIndex
          responseCount
          responsePercent
        }
      }
    }
  }
`;

export const SURVEY_DATA_FALSIFICATION_SUMMARY = gql`
  query surveyDataFalsificationSummary(
    $jobId: Int!
    $datasheetVersionId: Int!
  ) {
    surveyDataFalsificationSummary(
      jobId: $jobId
      datasheetVersionId: $datasheetVersionId
    ) {
      jobId
      datasheetVersionId
      totalResponses
      totalValid
      totalInvalid
      severityLevel
      isValid
      rulesSummary {
        rule
        failedResponses
      }
      falsificationAlertCount
      severityLevelPercent
      severityLevelAlert
    }
  }
`;

export const DATA_FALSIFICATION_RULES = gql`
  query dataFalsificationRules {
    dataFalsificationRules {
      rule
      color
      name
    }
  }
`;
